import cn from 'classnames';
import Backdrop from 'components/Common/Backdrop/Backdrop';
import LastOrderDate from 'components/Common/LastOrderDate/LastOrderDate';
import OpFavourite from 'components/Common/op-ui-wrappers/OpFavourite/OpFavourite';
import OpShippingInfo from 'components/Common/OpShippingInfo/OpShippingInfo';
import RecentlyViewed from 'components/Common/RecentlyViewed/RecentlyViewed';
import YouWillAlsoLike from 'components/Common/YouWillAlsoLike/YouWillAlsoLike';
import Layout from 'components/Layout/Layout';
import BackToSeeAllButton from 'components/ProductPage/BackToSeeAllButton/BackToSeeAllButton';
import BundleProducts from 'components/ProductPage/BundleProducts/BundleProducts';
import DeliveryOptions from 'components/ProductPage/DeliverOptions/DeliveryOptions';
import EstimatedShipmentPrice from 'components/ProductPage/EstimatedShipmentPrice/EstimatedShipmentPrice';
import OPPlusFreeShipping from 'components/ProductPage/OPPlusFreeShipping/OPPlusFreeShipping';
import OPPlusTransitionReminder from 'components/ProductPage/OPPlusTransitionReminder/OPPlusTransitionReminder';
import PersonaliseButton from 'components/ProductPage/PersonaliseButton/PersonaliseButton';
import PersonalisedProductDescription from 'components/ProductPage/PersonalisedProductDescription/PersonalisedProductDescription';
import PersonalisedProductDetails from 'components/ProductPage/PersonalisedProductDetails/PersonalisedProductDetails';
import PersonalisedProductTitle from 'components/ProductPage/PersonalisedProductTitle/PersonalisedProductTitle';
import ProductAttributeDescription from 'components/ProductPage/ProductAttributeDescription/ProductAttributeDescription';
import ProductAttributes from 'components/ProductPage/ProductAttributes/ProductAttributes';
import ProductionCountry from 'components/ProductPage/ProductionCountry/ProductionCountry';
import ProductPreviewDesktop from 'components/ProductPage/ProductPreviewDesktop/ProductPreviewDesktop';
import ProductPreviewMobile from 'components/ProductPage/ProductPreviewMobile/ProductPreviewMobile';
import ProductPrice from 'components/ProductPage/ProductPrice/ProductPrice';
import SupportMaterials from 'components/ProductPage/SupportMaterials/SupportMaterials';
import OpStickyBar from 'components/Common/OpStickyBar/OpStickyBar';
import UspsPremium from 'components/ProductPage/UspsPremium/UspsPremium';
import { WALLPAPER_PRODUCT_TYPES } from 'constants/app';
import useActiveRoute from 'hooks/useActiveRoute';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from 'store';
import isDesktop from 'store/app/selector/isDesktop';
import fetchCurrentCategoryOperation from 'store/category/operation/fetchCurrentCategoryOperation';
import sendGtmPersonalisedProductViewOperation from 'store/category/operation/sendGtmPersonalisedProductViewOperation';
import getCategoryId from 'store/category/selector/getCategoryId';
import getFetchedCategoryId from 'store/category/selector/getFetchedCategoryId';
import fetchPricesOperation from 'store/price/operation/fetchPricesOperation';
import fetchDeliveryDetailsOperation from 'store/product/operation/fetchDeliveryDetailsOperation';
import fetchDeliveryOptionsOperation from 'store/product/operation/fetchDeliveryOptionsOperation';
import fetchOPPlusContentOperation from 'store/product/operation/fetchOPPlusContentOperation';
import fetchPersonalisedProductOperation from 'store/product/operation/fetchPersonalisedProductOperation';
import selectProductBasedOnUrlParametersOperation from 'store/product/operation/selectProductBasedOnUrlParametersOperation';
import getPersonalisedProduct from 'store/product/selector/getPersonalisedProduct';
import getPersonalisedProductItem from 'store/product/selector/getPersonalisedProductItem';
import getProductId from 'store/product/selector/getProductId';
import { setPersonalisedProductDataAction } from 'store/product/slice';
import SSRParams from 'utils/class/SSRParams';
import gtmSendFunnelStepEvent, { FUNNEL_STEPS } from 'utils/gtm/gtmSendFunnelStepEvent';
import setPageMeta from 'utils/setPageMeta';
import scrollPageTop from 'utils/viewport/scrollPageTop';
import styles from './PersonalisedProductPage.module.scss';

const PersonalisedProductPage = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [shouldSendGtm, setShouldSendGtm] = useState(false);
  const [showDeliveryOptions, setShowDeliveryOptions] = useState(false);

  const categoryId = useSelector(getCategoryId);
  const isDesktopDevice = useSelector(isDesktop);
  const fetchedCategory = useSelector(getFetchedCategoryId);
  const product = useSelector(getPersonalisedProduct);
  const productItem = useSelector(getPersonalisedProductItem);
  const isWallpaper = WALLPAPER_PRODUCT_TYPES.includes(productItem?.product.productTypeId || -1);
  const priceRemark = isWallpaper ? '*' : undefined;
  const activeRoute = useActiveRoute();
  const productId = useSelector(getProductId);
  const theme = activeRoute?.isPersonalised ? 'personalised' : 'default';

  useEffect(() => {
    dispatch(
      setPersonalisedProductDataAction({
        productName: activeRoute?.productName,
        productUid: activeRoute?.productUid,
      }),
    );
  }, []);

  useEffect(() => {
    if (product?.seo) {
      setPageMeta(product.seo);
    }
  }, [product]);

  useEffect(() => {
    if (shouldSendGtm && productItem?.product && product?.abstractProduct) {
      dispatch(sendGtmPersonalisedProductViewOperation(product?.abstractProduct, productItem.product));
    }
  }, [product, shouldSendGtm]);

  useEffect(() => {
    scrollPageTop();

    if (fetchedCategory !== Number(categoryId)) {
      dispatch(fetchCurrentCategoryOperation());
    }

    dispatch(fetchPersonalisedProductOperation())
      .then(() => {
        gtmSendFunnelStepEvent(
          FUNNEL_STEPS.productPage,
          `/private/${product?.abstractProduct?.slug}/${activeRoute?.productName}`,
        );
        dispatch(selectProductBasedOnUrlParametersOperation());
        dispatch(fetchDeliveryDetailsOperation());
        dispatch(fetchDeliveryOptionsOperation());
      })
      .then(() => dispatch(fetchOPPlusContentOperation()))
      .finally(() => dispatch(fetchPricesOperation()))
      .then(() => setShouldSendGtm(true));
  }, [location]);

  return (
    <Layout
      itemType="http://schema.org/Product"
      itemScope
      className={styles.personalisedTheme}
      prefix={<OpStickyBar />}
    >
      <meta itemProp="brand" content="Optimalprint" />
      <div className={cn(styles.row, styles.seeAll)}>
        <BackToSeeAllButton primary />
      </div>
      <div className={cn(styles.personalisedProductPage, { [styles.mobile]: !isDesktopDevice })}>
        {isDesktopDevice && (
          <div className={styles.column}>
            <ProductPreviewDesktop />
            {productItem?.product.sku && (
              <div className={styles.favouriteIcon}>
                <OpFavourite
                  sku={productItem.product.sku}
                  categoryId={productItem.product.categoryId}
                  productTier={productItem.product.productTier}
                />
              </div>
            )}
          </div>
        )}
        {!isDesktopDevice && <ProductPreviewMobile showThumbnails />}
        <div className={cn(styles.column, styles.dataColumn)}>
          <div className={styles.productTitleRow}>
            <PersonalisedProductTitle />
          </div>
          <ProductPrice theme={theme} remark={priceRemark} />

          <PersonalisedProductDescription />

          <ProductAttributes theme={theme} />
          <BundleProducts theme={theme} />
          <div className={styles.buttonContainer}>
            <PersonaliseButton primary />
            {priceRemark && (
              <div className={cn(styles.priceRemark, styles[`theme-${theme}`])}>
                <sup>*</sup>
                {t('txt_price_per_square_meter')}
              </div>
            )}
          </div>

          <LastOrderDate type="sm" theme="default" productId={productId} />

          <OpShippingInfo
            className={styles.opShippingInfo}
            theme="blue"
            productId={productId}
            quantity={1}
            onReload={setShowDeliveryOptions}
          >
            <ProductionCountry />
            <EstimatedShipmentPrice theme={theme} />
          </OpShippingInfo>

          <div className={styles.deliveryDetails}>
            <OPPlusTransitionReminder />
            <OPPlusFreeShipping />
          </div>
          <UspsPremium />
          <PersonalisedProductDetails theme={theme} />

          {isWallpaper && (
            <ProductAttributeDescription
              theme={theme}
              key="wallpaperTypeDescription"
              selectorTypeName="UnifiedPaperType"
              sectionTitle={t('txt_paper_type_description')}
            />
          )}
          <ProductAttributeDescription
            theme={theme}
            key="phonecaseDescription"
            selectorTypeName="PhonecaseSubstrate"
            sectionTitle={t('txt_phonecase_style_description')}
          />
          {showDeliveryOptions && <DeliveryOptions theme={theme} />}
          <SupportMaterials />
        </div>
      </div>
      <div className={styles.space} />
      <div className={styles.row}>
        <YouWillAlsoLike />
      </div>
      <div className={styles.row}>
        <RecentlyViewed />
      </div>
      <Backdrop />
    </Layout>
  );
};

export default PersonalisedProductPage;

const fetchProductData = (params: SSRParams) => [params.dispatch(fetchPersonalisedProductOperation())];

// Executed on the server to prepare initial data
export const fetchPersonalisedProductPageInitialData = (params: SSRParams): Promise<any>[] => {
  // TODO: get params from URL, store them
  params.dispatch(
    setPersonalisedProductDataAction({
      productName: params.currentPageParams.productName,
      productUid: params.currentPageParams.productUid,
    }),
  );
  // Should return array of promises to resolve on the server
  return fetchProductData(params);
};
